import {Component, Inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {KeycloakService} from 'keycloak-angular';
import {LookupService} from '../../../../common/services/lookup.service';
import {HelpService} from '../../../../other/services/help.service';
import {HelpDTO} from '../../../../other/models/HelpDTO';
import {InsuredPersonService} from '../../../services/insured-person.service';
import {InsuredPersonDTO} from '../../../models/InsuredPersonDTO';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {ClmRequestBuilder} from '../../../../clm/services/clm-request-builder';
import {LocationService} from '../../../services/location.service';
import {LocationDTO} from '../../../../clm/models/LocationDTO';
import {BankAccountLinkDTO} from '../../../models/BankAccountLinkDTO';
import {BankAccountDTO} from '../../../models/BankAccountDTO';
import {Common} from '../../../../common/util/Util';
import {ProviderService} from '../../../services/provider.service';
import {ProviderDTO} from '../../../models/ProviderDTO';
import {CustomValidator} from '../../../../common';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';

@Component({
  selector: 'app-add-bank-account-dialog',
  templateUrl: './add-bank-account-dialog.component.html',
  styleUrls: ['../../../../../../styles/global/_dialog-styles.scss']
})
export class AddBankAccountDialogComponent implements OnInit {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;

  bankAccountFormGroup: FormGroup;
  bankFormControl: FormControl;
  branchRoutingNumberFormControl: FormControl;
  bankAccountTypeFormControl: FormControl;
  nameFormControl: FormControl;
  accountNumberFormControl: FormControl;
  ibanNumberFormControl: FormControl;
  currencyFormControl: FormControl;
  locationFormControl: FormControl;

  helpInfo: HelpDTO[];
  banks: NameValueDTO[];
  currencies: NameValueDTO[];
  bankAccountTypes: NameValueDTO[];
  insuredPerson: InsuredPersonDTO;
  provider: ProviderDTO;
  filteredLocations: LocationDTO[];
  selectedLocation: NameValueDTO;

  isEdit = false;
  deferSave = signal(false);
  userFirstLastName = '';
  heading = 'Add Bank Account';
  entityId: number;
  entityType: string;

  readonly locationTextChanged$ = new Subject<string>();

  constructor(
    public dialogRef: MatDialogRef<AddBankAccountDialogComponent>,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private keycloakService: KeycloakService,
    private lookupService: LookupService,
    private helpService: HelpService,
    private insuredPersonService: InsuredPersonService,
    private providerService: ProviderService,
    private locationService: LocationService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.entityId = data.entityId;
    this.entityType = data.entityType;
    if (data.insuredPerson) {
      this.deferSave.set(true);
      this.insuredPerson = data.insuredPerson;
    }

    this.bankFormControl = new FormControl('', Validators.required);
    this.branchRoutingNumberFormControl = new FormControl('', [Validators.required, CustomValidator.validNumber]);
    this.bankAccountTypeFormControl = new FormControl('', Validators.required);
    this.nameFormControl = new FormControl('', Validators.required);
    this.accountNumberFormControl = new FormControl('', [Validators.required, CustomValidator.validNumber]);
    this.ibanNumberFormControl = new FormControl('');
    this.currencyFormControl = new FormControl('Rand');
    this.locationFormControl = new FormControl('');

    this.bankAccountFormGroup = this.formBuilder.group({
      bank: this.bankFormControl,
      branchRoutingNumber: this.branchRoutingNumberFormControl,
      bankAccountType: this.bankAccountTypeFormControl,
      name: this.nameFormControl,
      accountNumber: this.accountNumberFormControl,
      ibanNumber: this.ibanNumberFormControl,
      currency: this.currencyFormControl,
      location: this.locationFormControl,
    });

    this.locationTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        debounceTime(this.debounceTime)
      )
      .subscribe((cityName: string) => {
          this.getListOfLocations(cityName);
        },
        error => console.log(error));
  }

  ngOnInit(): void {
    this.getListOfLookupValues(this.lookupValue.Currency);
    this.getListOfLookupValues(this.lookupValue.Bank);
    this.getListOfLookupValues(this.lookupValue.BankAccountType);

    this.keycloakService.loadUserProfile().then(profile => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });

    switch (this.entityType) {
      case 'Insured Person':
        if (this.deferSave() && this.insuredPerson?.bankAccounts?.length > 0) {
          this.setFormsForEdit(this.insuredPerson.bankAccounts[0].bankAccount, this.insuredPerson.lastName);
          break;
        }
        if (!this.deferSave()) {
          this.getInsuredPersonById(this.entityId);
        }
        break;
      case 'Provider':
        this.getProviderById(this.entityId);
        break;
    }

  }

  getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe(
      (response: NameValueDTO[]) => {
        if (entity === this.lookupValue.Currency) {
          this.currencies = response;
        }
        else if (entity === this.lookupValue.Bank) {
          this.banks = response;
        }
        else if (entity === this.lookupValue.BankAccountType) {
          this.bankAccountTypes = response;
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getInsuredPersonById(insuredPersonId: number): void {
    this.insuredPersonService.getInsuredPerson(insuredPersonId).subscribe(
      (response) => {
        this.insuredPerson = response;
        if (this.insuredPerson.bankAccounts != null && this.insuredPerson.bankAccounts.length > 0) {
          this.setFormsForEdit(this.insuredPerson.bankAccounts[0].bankAccount, this.insuredPerson.lastName);
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getProviderById(providerId: number): void {
    this.providerService.getProvider(providerId).subscribe(
      (response) => {
        this.provider = response;
        if (this.provider.bankAccount != null) {
          this.setFormsForEdit(this.provider.bankAccount, this.provider.name);
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getHelpInfo(componentName: string) {
    this.helpService.getListOfHelpInfo(componentName).subscribe(
      (response) => {
        this.helpInfo = response;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo != null) {
      const helpInfo = this.helpInfo.find((help) => help.inputName === inputName);
      message = helpInfo != null ? helpInfo.inputInfo : '';
    }
    return message;
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 10000
    });
  }

  onSaveClick(): void {
    if (this.bankAccountFormGroup.valid) {
      if (this.deferSave()) {
        const existingBankAccounts = this.insuredPerson?.bankAccounts || [];
        const bankAccountLinks = this.createBankAccountLinkPayload([...existingBankAccounts]);
        this.dialogRef.close(bankAccountLinks);
        return;
      }
      switch (this.entityType) {
        case 'Insured Person':
          this.saveInsuredPersonBankAccount();
          break;
        case 'Provider':
          this.saveProviderBankAccount();
          break;
      }
    }
    else {
      this.openSnackBar('Please complete all required fields.', 'Dismiss');
    }
  }

  private saveInsuredPersonBankAccount() {
    this.insuredPerson.bankAccounts = this.createBankAccountLinkPayload(this.insuredPerson.bankAccounts);
    this.insuredPerson.updated = new Date();
    this.insuredPerson.updatedBy = this.userFirstLastName;
    this.insuredPersonService.addInsuredPerson(this.insuredPerson).subscribe(
      (response) => {
        this.dialogRef.close(response);
        this.openSnackBar(`${response.bankAccounts[0].bankAccount.name} saved successfully.`, 'Dismiss');
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  private saveProviderBankAccount() {
    this.provider.bankAccount = this.createBankAccountPayload(this.provider.bankAccount);
    this.provider.updated = new Date();
    this.provider.updatedBy = this.userFirstLastName;
    this.providerService.addProvider(this.provider).subscribe(
      (response) => {
        this.dialogRef.close(response);
        this.openSnackBar(`${response.bankAccount.name} saved successfully.`, 'Dismiss');
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  createBankAccountLinkPayload(existingBankAccountLinks: BankAccountLinkDTO[]): BankAccountLinkDTO[] {
    const bankAccountLink = new BankAccountLinkDTO();
    const bankAccount = this.createBankAccountPayload(this.isEdit ? existingBankAccountLinks[0]?.bankAccount : null);
    if (this.isEdit) {
      bankAccountLink.created = existingBankAccountLinks[0].created;
      bankAccountLink.createdBy = existingBankAccountLinks[0].createdBy;
      bankAccountLink.updated = new Date();
      bankAccountLink.updatedBy = this.userFirstLastName;
    }
    else {
      bankAccountLink.created = new Date();
      bankAccountLink.createdBy = this.userFirstLastName;
    }
    bankAccountLink.bankAccount = bankAccount;

    switch (this.entityType) {
      case 'Insured Person':
        bankAccountLink.startDate = new Date();
        break;
    }

    existingBankAccountLinks = [];
    existingBankAccountLinks.push(bankAccountLink);

    return existingBankAccountLinks;
  }

  private createBankAccountPayload(existingBankAccount: BankAccountDTO) {
    const bankAccount = new BankAccountDTO();
    bankAccount.bank = this.banks.find(bank => bank.name === this.bankFormControl.value);
    bankAccount.branchRoutingNumber = this.branchRoutingNumberFormControl.value;
    bankAccount.bankAccountType = this.bankAccountTypes.find(type => type.name === this.bankAccountTypeFormControl.value);
    bankAccount.name = this.nameFormControl.value;
    bankAccount.accountNumber = this.accountNumberFormControl.value;
    bankAccount.ibanNumber = this.ibanNumberFormControl.value;
    bankAccount.currency = this.currencies.find(cur => cur.name === this.currencyFormControl.value);
    bankAccount.location = this.selectedLocation;

    if (this.isEdit) {
      bankAccount.bankAccountId = existingBankAccount.bankAccountId;
      bankAccount.created = existingBankAccount.created;
      bankAccount.createdBy = existingBankAccount.createdBy;
      bankAccount.updated = new Date();
      bankAccount.updatedBy = this.userFirstLastName;
    }
    else {
      bankAccount.created = new Date();
      bankAccount.createdBy = this.userFirstLastName;
    }

    return bankAccount;
  }

  locationSelected(name: any) {
    if (this.filteredLocations && this.filteredLocations.length > 0) {
      const location = this.filteredLocations.find((item: LocationDTO) => name === this.getLocationDisplayString(item));
      this.selectedLocation = {value: location.locationId, name: location.cityTown};
    }
  }

  clearLocation() {
    this.locationFormControl.reset();
    this.selectedLocation = null;
  }

  getLocationDisplayString(location: LocationDTO): string {
    return `${location.cityTown}, ${location.country?.name}`;
  }

  private getListOfLocations(cityName: string): void {
    const pagedRequestDTO = ClmRequestBuilder.getListOfLocationPagedRequestDTO(cityName);
    this.locationService.getListOfLocations(pagedRequestDTO).subscribe(
      (response) => {
        this.filteredLocations = response.resultList;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  private setFormsForEdit(bankAccountDTO: BankAccountDTO, name: string) {
    this.heading = `Edit Bank Account - ${name}`;
    this.isEdit = true;
    this.bankFormControl.setValue(bankAccountDTO.bank?.name);
    this.branchRoutingNumberFormControl.setValue(bankAccountDTO.branchRoutingNumber);
    this.bankAccountTypeFormControl.setValue(bankAccountDTO.bankAccountType?.name);
    this.nameFormControl.setValue(bankAccountDTO.name);
    this.accountNumberFormControl.setValue(bankAccountDTO.accountNumber);
    this.ibanNumberFormControl.setValue(bankAccountDTO.ibanNumber);
    this.currencyFormControl.setValue(bankAccountDTO.currency?.name);
    this.locationFormControl.setValue(bankAccountDTO.location?.name);
    this.selectedLocation = bankAccountDTO.location;
  }
}
